import React from 'react';
import { Link as RouterLink } from 'react-router';
import { Box, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import NavIcon from './NavIcon';
import NavItemChip from './NavItemChip';
import useNavColors from './navColors';

import type { ChipProps } from '@mui/material';

export interface NavItemProps {
    label: string;
    path: string;
    icon: string;
    isOpen: boolean;
    isActive: boolean;
    onClick?: () => void;
    /** Replaces the component the chip slot renders. */
    slots?: { chip?: React.ElementType };
    /** Props for the chip slot. The chip renders only when these are given. */
    slotProps?: { chip?: ChipProps };
}

/**
 * One row of the left navigation: an icon, a label, and a tooltip when the drawer is collapsed.
 */
const NavItem: React.FC<NavItemProps> = ({ label, path, icon, isOpen, isActive, onClick, slots, slotProps }) => {
    const navColors = useNavColors();
    const Chip = slots?.chip ?? NavItemChip;

    return (
        <ListItem disablePadding>
            <Tooltip title={!isOpen ? label : ''} placement="right">
                <ListItemButton
                    component={RouterLink}
                    to={path}
                    selected={isActive}
                    onClick={onClick}
                    sx={{
                        minHeight: 48,
                        alignItems: 'flex-start',
                        justifyContent: isOpen ? 'initial' : 'center',
                        color: navColors.linkColor,
                        borderBottom: `1px solid ${navColors.background}`,
                        '&:last-of-type': {
                            borderBottom: 'none',
                        },
                        '&.Mui-selected': {
                            color: navColors.linkActiveColor,
                            backgroundColor: navColors.linkActiveBgColor,
                            '& .MuiListItemText-primary': {
                                fontWeight: '600',
                            },
                            '&:hover': {
                                backgroundColor: navColors.linkHoverBgColor,
                                transition: 'all 0.3s ease',
                            },
                        },
                        '&:hover': {
                            backgroundColor: navColors.linkHoverBgColor,
                            color: navColors.linkColor,
                            transition: 'all 0.3s ease',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mt: 0.25,
                            justifyContent: 'center',
                        }}
                    >
                        <NavIcon icon={icon} isActive={isActive} />
                    </ListItemIcon>
                    <ListItemText
                        primary={label}
                        sx={{
                            opacity: isOpen ? 1 : 0,
                            ml: isOpen ? 0.75 : 0,
                            width: isOpen ? 'auto' : '0px',
                            whiteSpace: isOpen ? 'normal' : 'nowrap',
                            transition: (theme) =>
                                theme.transitions.create('opacity', {
                                    easing: theme.transitions.easing.easeInOut,
                                    duration: 10,
                                }),
                        }}
                    />
                    {isOpen && slotProps?.chip && (
                        <Box sx={{ display: 'flex', ml: 1, mt: 0.25 }}>
                            <Chip {...slotProps.chip} />
                        </Box>
                    )}
                </ListItemButton>
            </Tooltip>
        </ListItem>
    );
};

export default NavItem;
